/*
 * Knife finder quiz — site-wide triggers and loader.
 *
 * Loaded on every page by sections/quiz-finder-loader.liquid (footer group),
 * so it stays small. On first need it fetches, in parallel:
 *   - the modal markup, sections/quiz-finder-modal.liquid (a section on no
 *     template), through the Section Rendering API
 *   - the step logic, assets/component-quiz-finder-steps.js
 *
 * Triggers: any [data-quiz-open] element, any link to #quiz, and a page URL
 * that ends in #quiz. Prefetch on the first hover / focus / touchstart of a
 * trigger, or at idle on pages that carry a [data-quiz-open] button.
 */
(function () {
  if (window.yuzuQuiz) return;

  var SECTION_ID = 'quiz-finder-modal';
  var TRIGGER = '[data-quiz-open], a[href*="#quiz"]';
  var script = document.currentScript;
  var startProps = parseStartProps();
  var stepsSrc = script && script.dataset.stepsSrc;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var loading = null;
  var quiz = null;
  var opener = null;

  // "Quiz Started" event properties, set by sections/quiz-finder-loader.liquid.
  function parseStartProps() {
    try {
      return JSON.parse(script.dataset.startProps) || {};
    } catch (e) {
      return {};
    }
  }

  function isQuizLink(el) {
    if (el.hasAttribute('data-quiz-open')) return true;
    if (el.tagName !== 'A' || el.hash !== '#quiz') return false;
    return el.host === location.host;
  }

  function closestTrigger(target) {
    if (!target || typeof target.closest !== 'function') return null;
    var el = target.closest(TRIGGER);
    return el && isQuizLink(el) ? el : null;
  }

  // ── Fetch, inject, dedupe, retry ──────────────────────────────────

  function fetchSection() {
    return fetch(root + '?section_id=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('quiz section ' + r.status);
        return r.text();
      });
  }

  function loadSteps() {
    if (window.yuzuQuizSteps) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var tag = document.createElement('script');
      tag.src = stepsSrc;
      tag.onload = function () { resolve(); };
      tag.onerror = function () { tag.remove(); reject(new Error('quiz steps')); };
      document.head.appendChild(tag);
    });
  }

  function inject(html) {
    var existing = document.getElementById(SECTION_ID);
    if (existing) return existing;
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    var wrapper = tpl.content.firstElementChild;
    if (!wrapper || !wrapper.querySelector('#' + SECTION_ID)) throw new Error('quiz section empty');
    document.body.appendChild(wrapper);
    return document.getElementById(SECTION_ID);
  }

  function load() {
    if (quiz) return Promise.resolve(quiz);
    if (!loading) {
      loading = Promise.all([
        fetchSection().catch(fetchSection),
        loadSteps().catch(loadSteps)
      ])
        .then(function (parts) {
          quiz = window.yuzuQuizSteps(inject(parts[0]), {
            startProps: startProps,
            onClose: returnFocus
          });
          return quiz;
        })
        .catch(function (err) {
          loading = null;
          throw err;
        });
    }
    return loading;
  }

  function prefetch() {
    load().catch(function () {});
  }

  function returnFocus() {
    if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    opener = null;
  }

  function showError() {
    var el = document.createElement('div');
    el.className = 'quiz-finder-error text-size--small';
    el.setAttribute('role', 'status');
    el.textContent = "Sorry, the quiz didn't load. Please try again.";
    // Inline style: the modal section, and so its style, failed to load.
    el.style.cssText = 'position:fixed;left:50%;bottom:24px;z-index:9999;transform:translateX(-50%);max-width:calc(100% - 32px);padding:10px 16px;background:var(--color-text-main,#111);color:var(--color-background-main,#fff);border-radius:6px;';
    document.body.appendChild(el);
    setTimeout(function () { el.remove(); }, 4000);
  }

  function openQuiz(trigger) {
    opener = trigger || document.activeElement;
    if (trigger) trigger.setAttribute('aria-busy', 'true');
    load()
      .then(function (q) { q.open(); })
      .catch(showError)
      .then(function () {
        if (trigger) trigger.removeAttribute('aria-busy');
      });
  }

  // ── Triggers ──────────────────────────────────────────────────────

  // Capture phase, so the theme's own link and drawer handlers do not
  // navigate first.
  document.addEventListener('click', function (e) {
    var trigger = closestTrigger(e.target);
    if (!trigger) return;
    if (trigger.tagName === 'A' && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
    e.preventDefault();
    e.stopPropagation();
    var drawer = document.querySelector('sidebar-drawer.sidebar--opened');
    if (drawer && typeof drawer.hide === 'function') drawer.hide();
    openQuiz(trigger);
  }, true);

  var INTENT = ['pointerover', 'focusin', 'touchstart'];
  function onIntent(e) {
    if (!closestTrigger(e.target)) return;
    INTENT.forEach(function (type) { document.removeEventListener(type, onIntent, true); });
    prefetch();
  }
  INTENT.forEach(function (type) {
    document.addEventListener(type, onIntent, { capture: true, passive: true });
  });

  function idlePrefetch() {
    if (!document.querySelector('[data-quiz-open]')) return;
    if ('requestIdleCallback' in window) requestIdleCallback(prefetch, { timeout: 4000 });
    else setTimeout(prefetch, 2000);
  }

  function openFromHash() {
    if (location.hash === '#quiz') openQuiz(null);
  }

  window.addEventListener('hashchange', openFromHash);
  if (document.readyState === 'complete') {
    openFromHash();
    idlePrefetch();
  } else {
    window.addEventListener('load', function () {
      openFromHash();
      idlePrefetch();
    }, { once: true });
  }

  window.yuzuQuiz = { open: function () { openQuiz(null); }, prefetch: prefetch };
})();
